import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, Mail, Lock } from 'lucide-react';
import { useLegalSettings } from '../../hooks/useLegalSettings';
import { DISTRICTS } from '../../lib/districts';
import { AdSlot } from './ads/AdSlot';
import { useCmsContent } from '@/hooks/useCmsContent';

const BRAND = 'Marketplace TVDE';
const CONTACT_EMAIL = 'gestaofrotatvde@gmail.com';
const DOMAIN = 'marketplacetvde.pt';

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent('mp-open-cookie-prefs'));
}


export default function MarketplaceFooter({ onManageClick }) {
  const location = useLocation();
  const year = new Date().getFullYear();
  const { cms } = useCmsContent('rodape');
  const legal = useLegalSettings() || {};
  const owner = legal.denominacao_social || 'L Vizinha Unipessoal LDA';
  const sede = [legal.morada, legal.codigo_postal, legal.localidade].filter(Boolean).join(', ');
  const contactEmail = legal.email_geral || CONTACT_EMAIL;

  const isLegalPage = ['/termos', '/privacidade', '/cookies', '/aviso-legal'].includes(location.pathname);

  const legalLinks = [
    { to: '/termos', label: 'Termos e Condições', testId: 'footer-link-terms' },
    { to: '/privacidade', label: 'Política de Privacidade', testId: 'footer-link-privacy' },
    { to: '/cookies', label: 'Política de Cookies', testId: 'footer-link-cookies' },
    { to: '/aviso-legal', label: 'Aviso Legal', testId: 'footer-link-disclaimer' },
    { to: '/seguranca', label: 'Segurança e burlas', testId: 'footer-link-seguranca' },
    { to: '/parceiros', label: 'Parceiros e serviços', testId: 'footer-link-parceiros' },
    { to: '/noticias-tvde', label: 'Notícias TVDE', testId: 'footer-link-noticias' },
    { to: '/publicidade', label: 'Publicidade', testId: 'footer-link-publicidade' },
    { to: '/stands', label: 'Stands TVDE', testId: 'footer-link-stands' },
    { to: '/associacoes-tvde', label: 'Associações TVDE', testId: 'footer-link-associacoes' },
    { to: '/conversas', label: 'As minhas conversas', testId: 'footer-link-conversas' },
    { to: '/livre-resolucao', label: 'Livre resolução', testId: 'footer-link-livre-resolucao' },
    {
      href: 'https://www.livroreclamacoes.pt/inicio',
      label: 'Livro de Reclamações',
      testId: 'footer-link-livro-reclamacoes',
      external: true,
    },
    {
      href: 'https://www.consumidor.gov.pt/parceiros-em-acao-/rede-de-arbitragem-de-consumo.aspx',
      label: 'Resolução de Litígios',
      testId: 'footer-link-rl',
      external: true,
    },
  ];

  return (
    <footer
      className="mt-16 border-t border-slate-800 bg-slate-950 text-slate-300"
      data-testid="marketplace-footer"
    >
      <div className="mx-auto w-full max-w-6xl px-4 pt-6 md:px-6">
        <AdSlot posicao="global_pre_footer_banner" />
      </div>
      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        <div className="grid gap-10 md:grid-cols-3">
          {/* Col 1: Marca */}
          <div>
            <div className="flex items-center gap-2.5">
              <img
                src="/icon-192x192.png"
                alt={BRAND}
                className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-700"
              />
              <p className="text-base font-bold text-white" data-testid="footer-brand-name">
                {BRAND}
              </p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400" data-testid="footer-tagline">
              {cms('rodape_texto') || 'Marketplace português para anúncios de viaturas, motoristas e oportunidades TVDE.'}
            </p>
            <div className="mt-4 space-y-0.5 text-xs text-slate-500" data-testid="footer-company-info">
              <p>Operado por {owner}</p>
              {legal.nipc ? <p>NIPC {legal.nipc}</p> : null}
              {sede ? <p>{sede}</p> : null}
            </div>
          </div>

          {/* Col 2: Legal */}
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-400">
              Legal
            </p>
            <ul className="space-y-2.5 text-sm">
              {legalLinks.map((link) =>
                link.external ? (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-300 transition hover:text-emerald-400"
                      data-testid={link.testId}
                    >
                      {link.label}
                    </a>
                  </li>
                ) : (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-slate-300 transition hover:text-emerald-400"
                      data-testid={link.testId}
                    >
                      {link.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Col 3: Contacto */}
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-400">
              Contacto
            </p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 text-sm text-slate-300 transition hover:text-emerald-400"
              data-testid="footer-link-email"
            >
              <Mail className="h-4 w-4 text-emerald-400" />
              {contactEmail}
            </a>

            {onManageClick && (
              <>
                <p className="mt-6 mb-3 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  O meu anúncio
                </p>
                <button
                  onClick={onManageClick}
                  className="inline-block text-sm text-slate-300 transition hover:text-emerald-400"
                  data-testid="footer-manage-listing-btn"
                >
                  Gerir / Apagar o meu anúncio →
                </button>
              </>
            )}
          </div>
        </div>

        {/* Explorar por distrito (SEO / navegação) */}
        <div className="mt-10 border-t border-slate-800 pt-6" data-testid="footer-districts">
          <p className="text-sm font-bold text-white">Viaturas TVDE por distrito</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            {DISTRICTS.map((d) => (
              <Link
                key={`v-${d.slug}`}
                to={`/viaturas/${d.slug}`}
                className="text-xs text-slate-400 transition hover:text-emerald-400"
                data-testid={`footer-district-viatura-${d.slug}`}
              >
                {d.label}
              </Link>
            ))}
          </div>
          <p className="mt-5 text-sm font-bold text-white">Motoristas TVDE por distrito</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            {DISTRICTS.map((d) => (
              <Link
                key={`m-${d.slug}`}
                to={`/motoristas/${d.slug}`}
                className="text-xs text-slate-400 transition hover:text-emerald-400"
                data-testid={`footer-district-motorista-${d.slug}`}
              >
                {d.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Nota de independência */}
        <p className="mt-8 text-[11px] leading-relaxed text-slate-500" data-testid="footer-independence">
          O Marketplace TVDE é uma plataforma independente e não é afiliado, patrocinado ou representado
          pela Uber ou pela Bolt. As marcas e logótipos pertencem aos respectivos titulares.
        </p>

        {/* Linha inferior */}
        <div className="mt-10 flex flex-col gap-2 border-t border-slate-800 pt-5 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-copyright">
            © {year} {BRAND}. Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              onClick={openCookiePreferences}
              className="inline-flex items-center gap-1.5 text-slate-400 transition hover:text-emerald-400"
              data-testid="footer-manage-cookies"
            >
              <Cookie className="h-3.5 w-3.5" /> Gerir preferências de cookies
            </button>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-slate-400 transition hover:text-emerald-400"
              data-testid="footer-admin-login"
            >
              <Lock className="h-3.5 w-3.5" /> Área reservada
            </Link>
            <span className="text-slate-600">Marketplace · {DOMAIN}</span>
          </div>
        </div>
      </div>

      {!isLegalPage && <CookieBanner />}
    </footer>
  );
}

const CONSENT_KEY = 'mp_cookie_consent_v2';
const CONSENT_VERSION = 1;

function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
  } catch {
    return null;
  }
}

/** Banner de consentimento de cookies (Consent Mode v2, granular). */
function CookieBanner() {
  const [show, setShow] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (!saved) setShow(true);

    const openPrefs = () => {
      const cur = readConsent();
      setAnalytics(!!cur?.categories?.analytics);
      setAds(!!cur?.categories?.ads);
      setCustomize(true);
      setShow(true);
    };
    window.addEventListener('mp-open-cookie-prefs', openPrefs);
    return () => window.removeEventListener('mp-open-cookie-prefs', openPrefs);
  }, []);

  const persist = (categories) => {
    const id =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ version: CONSENT_VERSION, categories, at: new Date().toISOString(), id })
      );
    } catch (e) {
      console.warn('localStorage indisponível', e);
    }
    if (typeof window.__applyConsent === 'function') window.__applyConsent(categories);
    window.dispatchEvent(new Event('mp-consent-changed'));
    // Regista a decisão (anónima) para estatísticas no painel admin
    try {
      const action =
        categories.analytics && categories.ads
          ? 'accept_all'
          : !categories.analytics && !categories.ads
          ? 'reject_all'
          : 'save';
      fetch('/api/marketplace/consent-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analytics: !!categories.analytics, ads: !!categories.ads, action, source: 'prefs' }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {}
    setShow(false);
    setCustomize(false);
  };

  const acceptAll = () => persist({ analytics: true, ads: true });
  const rejectAll = () => persist({ analytics: false, ads: false });
  const saveCustom = () => persist({ analytics, ads });

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white px-4 py-4 shadow-2xl"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-6xl">
        {!customize ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="flex-1 text-xs leading-relaxed text-slate-600">
              <Cookie className="mr-1 inline h-4 w-4 text-emerald-600" />
              Usamos cookies essenciais ao funcionamento do site e, com a sua autorização, cookies de
              análise e publicidade. Pode aceitar tudo, recusar os não essenciais ou personalizar. Saiba mais na{' '}
              <Link to="/cookies" className="font-semibold text-emerald-600 underline hover:text-emerald-700">
                Política de Cookies
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCustomize(true)}
                data-testid="cookie-banner-customize"
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Personalizar
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-banner-essential"
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Apenas essenciais
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-emerald-700"
              >
                Aceitar tudo
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm font-bold text-slate-900">Preferências de cookies</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Essenciais</span>
                  <span className="text-[10px] font-semibold uppercase text-emerald-600">Sempre ativos</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Necessários ao funcionamento e segurança do site.</p>
              </div>
              <label className="flex cursor-pointer flex-col rounded-lg border border-slate-200 bg-white p-3" data-testid="cookie-toggle-analytics-label">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Análise</span>
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} data-testid="cookie-toggle-analytics" />
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Google Analytics e PostHog — estatísticas de utilização.</p>
              </label>
              <label className="flex cursor-pointer flex-col rounded-lg border border-slate-200 bg-white p-3" data-testid="cookie-toggle-ads-label">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Publicidade</span>
                  <input type="checkbox" checked={ads} onChange={(e) => setAds(e.target.checked)} data-testid="cookie-toggle-ads" />
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Google Ads — medição de campanhas.</p>
              </label>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={rejectAll}
                data-testid="cookie-prefs-reject"
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Recusar tudo
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-prefs-accept-all"
                className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100"
              >
                Aceitar tudo
              </button>
              <button
                onClick={saveCustom}
                data-testid="cookie-prefs-save"
                className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-emerald-700"
              >
                Guardar preferências
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
